import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import AppSubscriptionButton from "@/components/payment/AppSubscriptionButton";
import { AuthWrapper } from "@/components/layout/AuthWrapper";

const SubscriptionCheckout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading user data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSkip = () => {
    // Route based on user role
    if (user?.user_metadata?.is_superuser || user?.user_metadata?.role === 'superadmin') {
      navigate("/superadmin");
    } else if (user?.user_metadata?.role === 'parent') {
      navigate("/parent-portal");
    } else {
      navigate("/admin");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthWrapper>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="flex-1 container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-3xl font-bold mb-4">Complete Your Setup</h1>
              <p className="text-lg text-gray-600">
                Choose your subscription plan to get started
              </p>
            </div>

            <Card className="shadow-lg border-2 border-primary/20 mb-8">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-bold">Platform Plan</CardTitle>
                <CardDescription className="text-base mt-2">
                  Everything you need to manage your programs
                </CardDescription>
                <div className="mt-4">
                  <span className="text-3xl font-bold">$49</span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                <div className="text-sm text-primary mt-1">14-day free trial</div>
              </CardHeader>
              <CardContent className="space-y-3 pb-6">
                <div className="space-y-2">
                  {[
                    "Unlimited students and families",
                    "Class scheduling and management",
                    "Family and student profiles",
                    "Attendance tracking",
                    "Payment processing",
                    "Email notifications",
                    "Parent portal access",
                    "Mobile-friendly interface",
                    "Priority support"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center">
                      <Check className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <AppSubscriptionButton
                  priceId="price_1PWl6GFwQOyRjomcE6Eqkp3b"
                  className="w-full py-5 bg-primary hover:bg-primary/90"
                >
                  Start 14-Day Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                </AppSubscriptionButton>
                <Button variant="link" onClick={handleSkip}>
                  Skip for now (trial starts automatically)
                </Button>
              </CardFooter>
            </Card>

            <div className="text-center text-sm text-gray-500 max-w-lg mx-auto">
              <p className="mb-2">
                Your 14-day free trial starts today. You won't be charged until the trial ends.
              </p>
              <p>
                By subscribing, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default SubscriptionCheckout;
//comment